import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { EpocasProvider } from './context/EpocasContext';
import { AuthProvider, useAuthContext } from './context/AuthContext';
import { Analytics } from '@vercel/analytics/react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './services/firebaseConfig';
import supabase from './services/SupabaseService';

import Navbar from './components/Navbar';
import BottomBar from './components/BottomBar';
const Home = lazy(() => import('./components/Home'));
const Jogos = lazy(() => import('./components/Jogos'));
const Plantel = lazy(() => import('./components/Plantel'));
const EstatisticasJogador = lazy(() => import('./components/EstatisticasJogador'));
const EstatisticasEquipa = lazy(() => import('./components/EstatisticasEquipa'));
const EquipaTecnica = lazy(() => import('./components/EquipaTecnica'));
const Direcao = lazy(() => import('./components/Direcao'));
const Socios = lazy(() => import('./components/Socios'));
const HistoriaClube = lazy(() => import('./components/HistoriaClube'));
const Patrocinadores = lazy(() => import('./components/Patrocinadores'));
const PlanteisAnteriores = lazy(() => import('./components/PlanteisAnteriores'));
const Login = lazy(() => import('./components/LoginModal'));
const MeusDados = lazy(() => import('./components/MeusDados'));

function ToastLogin() {
  const { mostrarToast, setMostrarToast } = useAuthContext();

  useEffect(() => {
    if (!mostrarToast) return undefined;

    const timer = setTimeout(() => {
      setMostrarToast(false);
    }, 2500);

    return () => clearTimeout(timer);
  }, [mostrarToast, setMostrarToast]);

  if (!mostrarToast) return null;

  return (
    <div className="gd-login-toast" role="status">
      <span className="gd-login-toast-dot" />
      Login efetuado com sucesso!
    </div>
  );
}

function EstatisticasProtegidas({ children }) {
  const { loading, podeVerEstatisticas } = useAuthContext();
  if (loading) return <p>A verificar permissões...</p>;
  if (!podeVerEstatisticas) return <p>Não tens acesso às estatísticas desta época.</p>;
  return children;
}

function DadosProtegidos({ children }) {
  const { loading, isJogador, isAdmin } = useAuthContext();
  if (loading) return <p>A verificar permissões...</p>;
  if (!isJogador && !isAdmin) return <p>Não tens acesso aos dados pessoais desta época.</p>;
  return children;
}

// TESTE TEMPORÁRIO: remover depois de confirmar a autenticação no Supabase.
function TesteAutenticacaoSupabase() {
  useEffect(() => {
    let ativo = true;
    const unsubscribe = onAuthStateChanged(auth, async (utilizador) => {
      if (!utilizador || !ativo) return;
      try {
        await utilizador.getIdToken(true);
        const { data, error } = await supabase.rpc('verificar_autenticacao');
        if (!ativo) return;
        if (error) {
          console.error('TESTE SUPABASE - erro:', error.message);
        } else {
          console.log("TESTE SUPABASE:", {
            role: data?.role,
            uidPresente: data?.uid_presente,
            emailPresente: data?.email_presente,
            emailVerificado: data?.email_verificado,
            error: error?.message || null
          });
        }
      } catch (erro) {
        if (ativo) console.error('TESTE SUPABASE - falhou:', erro.message);
      }
      const { data: admin, error: erroAdmin } = await supabase.rpc(
        'verificar_admin'
      );

      if (!ativo) return;

      console.log('TESTE ADMIN:', {
        isAdmin: admin,
        error: erroAdmin?.message || null
      });
    });
    return () => { ativo = false; unsubscribe(); };
  }, []);
  return null;
}

function AppContent() {
  return (
    <div className="gd-app">
      <Navbar />

      <ToastLogin />
      <TesteAutenticacaoSupabase />

      <main className="gd-main">
        <Suspense fallback={<p role="status" style={{padding: 20}}>A carregar página...</p>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/jogos" element={<Jogos />} />
          <Route path="/plantel" element={<Plantel />} />
          <Route
            path="/estatisticas-jogador"
            element={<EstatisticasProtegidas><EstatisticasJogador /></EstatisticasProtegidas>}
          />
          <Route
            path="/estatisticas-equipa"
            element={<EstatisticasProtegidas><EstatisticasEquipa /></EstatisticasProtegidas>}
          />
          <Route path="/equipa-tecnica" element={<EquipaTecnica />} />
          <Route path="/direcao" element={<Direcao />} />
          <Route path="/socios" element={<Socios />} />
          <Route path="/historia-clube" element={<HistoriaClube />} />
          <Route
            path="/planteis-anteriores"
            element={<PlanteisAnteriores />}
          />
          <Route path="/patrocinadores" element={<Patrocinadores />} />
          <Route path="/login" element={<Login />} />
          <Route path="/meus-dados" element={<DadosProtegidos><MeusDados /></DadosProtegidos>} />
        </Routes>
        </Suspense>
      </main>

      <BottomBar />

      <style>{`
        * {
          box-sizing: border-box;
        }

        html {
          min-height: 100%;
          background: #1b1d20;
        }

        body {
          margin: 0;
          min-width: 320px;
          min-height: 100%;
          color: #f4f4f4;
          font-family: 'Montserrat', 'Segoe UI', Arial, sans-serif;
          background:
            radial-gradient(
              ellipse at 50% 0%,
              rgba(115, 120, 128, 0.18),
              transparent 58%
            ),
            linear-gradient(
              180deg,
              #25282d 0%,
              #1b1d20 55%,
              #22252a 100%
            );
          background-attachment: fixed;
        }

        button,
        input,
        select,
        textarea {
          font: inherit;
        }

        a {
          -webkit-tap-highlight-color: transparent;
        }

        .gd-app {
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          color: #f4f4f4;
          background: transparent;
        }

        .gd-main {
          flex: 1 0 auto;
          width: 100%;
          min-width: 0;
          padding: 24px;
        }

        .gd-login-toast {
          position: fixed;
          top: 96px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2000;
          display: flex;
          align-items: center;
          gap: 10px;
          max-width: calc(100vw - 32px);
          padding: 13px 20px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 12px;
          color: #ffffff;
          background: rgba(35, 38, 42, 0.94);
          box-shadow: 0 12px 36px rgba(0, 0, 0, 0.28);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          font-size: 14px;
          font-weight: 500;
          animation: gd-toast-in 0.25s ease-out;
        }

        .gd-login-toast-dot {
          width: 8px;
          height: 8px;
          flex: 0 0 8px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.1);
        }

        @keyframes gd-toast-in {
          from {
            opacity: 0;
            transform: translate(-50%, -8px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }

        @media (max-width: 700px) {
          .gd-main {
            padding: 16px 12px;
          }

          .gd-login-toast {
            top: 88px;
            padding: 12px 16px;
            font-size: 13px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            scroll-behavior: auto !important;
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <EpocasProvider>
      <AuthProvider>
        <Router>
          <AppContent />
          <Analytics />
        </Router>
      </AuthProvider>
    </EpocasProvider>
  );
}

export default App;